import { useState, useMemo, useCallback } from "react";

/**
 * Hook for searching, filtering and highlighting text in transcripts
 * Reuses debounce pattern from useDebounceSearch
 * @param {string} searchText - The text to search for
 * @param {Array} transcriptData - Array of transcript paragraphs
 * @returns {Object} { matches, currentMatchIndex, visibleParagraphs, isFiltering, etc. }
 */
const useTranscriptSearch = (searchText, transcriptData = []) => {
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);

  // Find all matches in the transcript data AND determine visibility
  const { matches, visibleParagraphs } = useMemo(() => {
    if (!searchText || !transcriptData?.length) {
      return {
        matches: [],
        visibleParagraphs: transcriptData.map((_, idx) => idx), // Show all if no search
      };
    }

    const searchLower = searchText.toLowerCase();
    const foundMatches = [];
    const visibleIndices = new Set();

    transcriptData.forEach((paragraph, paragraphIndex) => {
      // Handle both string and array text formats
      const textContent = typeof paragraph.text === "string"
        ? paragraph.text
        : Array.isArray(paragraph.text)
        ? paragraph.text.map(t => typeof t === "string" ? t : t.text || "").join(" ")
        : "";

      if (!textContent) return;

      const textLower = textContent.toLowerCase();
      let searchIndex = 0;
      let hasMatch = false;

      // Find all occurrences in this paragraph
      while (true) {
        const matchIndex = textLower.indexOf(searchLower, searchIndex);
        if (matchIndex === -1) break;

        hasMatch = true;
        visibleIndices.add(paragraphIndex); // Mark paragraph as visible

        foundMatches.push({
          paragraphIndex,
          startPos: matchIndex,
          endPos: matchIndex + searchText.length,
          text: textContent.substring(matchIndex, matchIndex + searchText.length),
          fullText: textContent,
          speaker: paragraph.speaker,
          start: paragraph.start,
          end: paragraph.end,
        });

        searchIndex = matchIndex + 1;
      }
    });

    return {
      matches: foundMatches,
      visibleParagraphs: Array.from(visibleIndices).sort((a, b) => a - b),
    };
  }, [searchText, transcriptData]);

  // Check if a paragraph should be visible based on search
  const isParagraphVisible = useCallback(
    (paragraphIndex) => {
      if (!searchText) return true; // Show all if no search
      return visibleParagraphs.includes(paragraphIndex);
    },
    [searchText, visibleParagraphs]
  );

  // Reset index when search changes
  const reset = useCallback(() => {
    setCurrentMatchIndex(0);
  }, []);

  // Navigate to next match
  const nextMatch = useCallback(() => {
    if (matches.length === 0) return;
    setCurrentMatchIndex((prev) => (prev + 1) % matches.length);
  }, [matches.length]);

  // Navigate to previous match
  const prevMatch = useCallback(() => {
    if (matches.length === 0) return;
    setCurrentMatchIndex((prev) => (prev - 1 + matches.length) % matches.length);
  }, [matches.length]);

  return {
    matches,
    currentMatchIndex,
    currentMatch: matches[currentMatchIndex] || null,
    matchCount: matches.length,
    nextMatch,
    prevMatch,
    reset,
    isSearchActive: Boolean(searchText && matches.length > 0),
    visibleParagraphs,              // Array of visible paragraph indices
    isParagraphVisible,             // Function to check if paragraph is visible
    isFiltering: Boolean(searchText), // Boolean indicating if filtering is active
  };
};

export default useTranscriptSearch;
